import { API_ENDPOINTS } from "@/constants/api";
import { MONTHLY_QUOTA_KEYS, RETIRED_MONTHLY_QUOTA_KEYS, type MonthlyQuotaKey } from "@/constants/quotas";
import type {
  FeatureUsageStatsResponse as GeneratedFeatureStatsResponse,
  PopularItemsResponse as GeneratedPopularItemsResponse,
  UsageStatsResponse,
} from "@sakura-tech-lab/dashboard-api";

import { statisticsApi } from "@/services/dashboard-api.client";

import { isUtcCalendarDate, parseProductProcessingUsage, type ProductProcessingUsage } from "@/lib/product-processing-usage";
import { USAGE_ANALYTICS_UNAVAILABLE, USAGE_RESPONSE_INVALID } from "@/lib/usage-messages";

export interface MonthlyUsageStats {
  period: string;
  merchantApi: number;
  youzuLens: number;
  youzuLensUsers: number;
  objectDetection: number;
  objectDetectionUsers: number;
  roomVisualizer: number;
  roomVisualizerUsers: number;
  propertyVisualize: number;
  propertyVisualizeUsers: number;
  propertyVisualizeRestyle?: number;
  propertyVisualizeRestyleUsers?: number;
  propertyVisualizeLeanRoom?: number;
  propertyVisualizeLeanRoomUsers?: number;
  propertyVisualizeCleanRoom?: number;
  propertyVisualizeCleanRoomUsers?: number;
  propertyVisualizeVideo?: number;
  propertyVisualizeVideoUsers?: number;
  propertyVisualizeCameraAngle?: number;
  propertyVisualizeCameraAngleUsers?: number;
  magicBackground: number;
  magicBackgroundUsers: number;
  twoDTo3D: number;
  twoDTo3DUsers: number;
  swap: number;
  swapUsers: number;
}

export interface MonthlyStatsResponse {
  success: boolean;
  data: MonthlyUsageStats[];
  requestId: string;
}

export interface FeatureUsageStats {
  feature: string;
  count: number;
  previousCount?: number;
  change?: number;
}

export interface FeatureStatsResponse {
  success: boolean;
  data: FeatureUsageStats[];
  requestId: string;
}

export interface PopularItem {
  name: string;
  count: number;
  percentage: number;
}

export interface PopularItemsResponse {
  success: boolean;
  data: PopularItem[];
  requestId: string;
}

const normalizeUsageStats = (response: UsageStatsResponse): MonthlyStatsResponse => ({
  success: response.success ?? false,
  data: (response.data ?? []).map((stat) => ({
    period: stat.period ?? "",
    merchantApi: stat.merchantApi ?? 0,
    youzuLens: stat.youzuLens ?? 0,
    youzuLensUsers: stat.youzuLensUsers ?? 0,
    objectDetection: stat.objectDetection ?? 0,
    objectDetectionUsers: stat.objectDetectionUsers ?? 0,
    roomVisualizer: stat.roomVisualizer ?? 0,
    roomVisualizerUsers: stat.roomVisualizerUsers ?? 0,
    propertyVisualize: stat.propertyVisualize ?? 0,
    propertyVisualizeUsers: stat.propertyVisualizeUsers ?? 0,
    propertyVisualizeRestyle: stat.propertyVisualizeRestyle,
    propertyVisualizeRestyleUsers: stat.propertyVisualizeRestyleUsers,
    propertyVisualizeCleanRoom: stat.propertyVisualizeCleanRoom,
    propertyVisualizeCleanRoomUsers: stat.propertyVisualizeCleanRoomUsers,
    propertyVisualizeVideo: stat.propertyVisualizeVideo,
    propertyVisualizeVideoUsers: stat.propertyVisualizeVideoUsers,
    propertyVisualizeCameraAngle: stat.propertyVisualizeCameraAngle,
    propertyVisualizeCameraAngleUsers: stat.propertyVisualizeCameraAngleUsers,
    magicBackground: stat.magicBackground ?? 0,
    magicBackgroundUsers: stat.magicBackgroundUsers ?? 0,
    twoDTo3D: stat.twoDTo3D ?? 0,
    twoDTo3DUsers: stat.twoDTo3DUsers ?? 0,
    swap: stat.swap ?? 0,
    swapUsers: stat.swapUsers ?? 0,
  })),
  requestId: response.requestId ?? "",
});

const normalizeFeatureStats = (
  response: GeneratedFeatureStatsResponse,
): FeatureStatsResponse => ({
  success: response.success ?? false,
  data: (response.data ?? []).map((stat) => ({
    feature: stat.feature ?? "",
    count: stat.count ?? 0,
    previousCount: stat.previousCount,
    change: stat.percentageChange,
  })),
  requestId: response.requestId ?? "",
});

const normalizePopularItems = (
  response: GeneratedPopularItemsResponse,
): PopularItemsResponse => ({
  success: response.success ?? false,
  data: (response.data ?? []).map((item) => ({
    name: item.name ?? "",
    count: item.count ?? 0,
    percentage: item.percentage ?? 0,
  })),
  requestId: response.requestId ?? "",
});

export const getMonthlyStatistics = async (
  year: number = new Date().getUTCFullYear()
): Promise<MonthlyStatsResponse> => {
  return normalizeUsageStats(await statisticsApi.getMonthlyStatistics({ year }));
};

export const getDailyStatistics = async (
  year: number = new Date().getUTCFullYear(),
  month?: number
): Promise<MonthlyStatsResponse> => {
  return normalizeUsageStats(await statisticsApi.getDailyStatistics({ year, month }));
};

const getDailyStatisticsForRange = async (
  startDaysAgo: number,
  endDaysAgo: number,
): Promise<MonthlyStatsResponse> => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setUTCDate(today.getUTCDate() - startDaysAgo);
  const endDate = new Date(today);
  endDate.setUTCDate(today.getUTCDate() - endDaysAgo);

  // Collect all unique year-month combos needed
  const monthsNeeded = new Map<string, { year: number; month: number }>();
  const cursor = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1));
  while (cursor <= endDate) {
    const key = `${cursor.getUTCFullYear()}-${cursor.getUTCMonth() + 1}`;
    monthsNeeded.set(key, { year: cursor.getUTCFullYear(), month: cursor.getUTCMonth() + 1 });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  const responses = await Promise.all(
    Array.from(monthsNeeded.values()).map(({ year, month }) =>
      getDailyStatistics(year, month)
    ),
  );

  const failedResponse = responses.find((response) => !response.success);
  if (failedResponse) {
    // Retain the first failing upstream request's correlation ID for triage.
    return { success: false, data: [], requestId: failedResponse.requestId };
  }
  const allData = responses.flatMap((r) => r.data);
  const startStr = startDate.toISOString().slice(0, 10);
  const endStr = endDate.toISOString().slice(0, 10);
  const filteredData = allData.filter(
    (stat) => stat.period >= startStr && stat.period <= endStr,
  );

  return { success: true, data: filteredData, requestId: "" };
};

export const getDailyStatisticsLast30Days = (): Promise<MonthlyStatsResponse> =>
  getDailyStatisticsForRange(29, 0);

export const getDailyStatisticsPrevious30Days = (): Promise<MonthlyStatsResponse> =>
  getDailyStatisticsForRange(59, 30);

export const getFeatureStatistics = async (
  startDate: Date,
  endDate: Date,
  includePrevious: boolean = true
): Promise<FeatureStatsResponse> => {
  return normalizeFeatureStats(await statisticsApi.getFeatureStatistics({
    startDate,
    endDate,
    includePrevious,
  }));
};

const getMonthStats = async (month: Date): Promise<MonthlyUsageStats | null> => {
  const response = await getMonthlyStatistics(month.getUTCFullYear());
  if (!response.success) throw Object.assign(new Error(USAGE_ANALYTICS_UNAVAILABLE), { cause: response });
  return response.data.find(stat => stat.period === month.toISOString().slice(0, 7)) ?? null;
};

export const getCurrentMonthStats = (): Promise<MonthlyUsageStats | null> => getMonthStats(new Date());

export const getPreviousMonthStats = (): Promise<MonthlyUsageStats | null> => {
  const now = new Date();
  const previousMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return getMonthStats(previousMonth);
};

export const getPopularRoomStyles = async (
  startDate: string,
  endDate: string,
  limit: number = 10
): Promise<PopularItemsResponse> => {
  return normalizePopularItems(await statisticsApi.listPopularRoomStyles({
    startDate: new Date(startDate),
    endDate: new Date(endDate),
    limit,
  }));
};

export const getPopularRoomTypes = async (
  startDate: string,
  endDate: string,
  limit: number = 10
): Promise<PopularItemsResponse> => {
  return normalizePopularItems(await statisticsApi.listPopularRoomTypes({
    startDate: new Date(startDate),
    endDate: new Date(endDate),
    limit,
  }));
};

export const getPopularLensLabels = async (
  startDate: string,
  endDate: string,
  limit: number = 10
): Promise<PopularItemsResponse> => {
  return normalizePopularItems(await statisticsApi.listPopularLensSearchLabels({
    startDate: new Date(startDate),
    endDate: new Date(endDate),
    limit,
  }));
};

type PlanFeatureUsage = { used: number; limit: number | null };
const monthlyQuotaKeys = new Set<string>(MONTHLY_QUOTA_KEYS);

export interface MonthlyPlanUsage {
  productProcessing?: ProductProcessingUsage;
  period: { startDate: string; endDate: string };
  features: Partial<Record<MonthlyQuotaKey, PlanFeatureUsage>> & { merchantApi: PlanFeatureUsage };
  degraded: false; // Degraded responses are rejected as unavailable.
}

function isFeatureUsage(value: unknown): value is PlanFeatureUsage {
  if (!value || typeof value !== "object") return false;
  const entry = value as { used?: number; limit?: number | null };
  return typeof entry.used === "number" && Number.isSafeInteger(entry.used) && entry.used >= 0 &&
    (entry.limit === null || (typeof entry.limit === "number" && Number.isSafeInteger(entry.limit) && entry.limit >= 0));
}

export async function getMonthlyPlanUsage(): Promise<MonthlyPlanUsage> {
  try {
    // Validate the raw usage response before displaying counts or allowances.
    const res = await fetch(API_ENDPOINTS.STATISTICS.USAGE, { credentials: "include" });
    if (!res.ok) throw Object.assign(new Error(USAGE_ANALYTICS_UNAVAILABLE), { cause: `HTTP ${res.status}` });
    const body = await res.json() as Partial<MonthlyPlanUsage> | null;
    if (body?.features && typeof body.features === "object") {
      body.features = Object.fromEntries(Object.entries(body.features)
        .filter(([key]) => !RETIRED_MONTHLY_QUOTA_KEYS.includes(key))) as MonthlyPlanUsage["features"];
    }
    if (body?.degraded !== false || !isFeatureUsage(body.features?.merchantApi) ||
        !Object.entries(body.features ?? {}).every(([key, value]) => monthlyQuotaKeys.has(key) && isFeatureUsage(value)) ||
        !isUtcCalendarDate(body.period?.startDate) || !isUtcCalendarDate(body.period?.endDate) ||
        body.period.startDate > body.period.endDate) {
      throw Object.assign(new Error(USAGE_ANALYTICS_UNAVAILABLE), { cause: USAGE_RESPONSE_INVALID });
    }
    const productProcessing = parseProductProcessingUsage(body.productProcessing);
    // Absent and null both mean "not reported"; only a malformed value is worth a warning.
    if (body.productProcessing != null && productProcessing === undefined) {
      console.warn("Product processing usage unavailable", { cause: "Invalid productProcessing response" });
    }
    return { ...body, productProcessing } as MonthlyPlanUsage;
  } catch (cause) {
    if (cause instanceof Error && cause.message === USAGE_ANALYTICS_UNAVAILABLE) throw cause;
    throw Object.assign(new Error(USAGE_ANALYTICS_UNAVAILABLE), { cause });
  }
}
